import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { isAuthApiError } from '@supabase/supabase-js';
import { useAuth } from '../../contexts/AuthContext';
import { LogIn, Mail, Lock, AlertCircle, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRateLimiter } from '../../hooks/useRateLimiter';
import { logger } from '../../lib/logger';
import { SuspensionNotice } from './SuspensionNotice';

export function LoginForm() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [suspended, setSuspended] = useState(false);

  // Rate limiting: 5 attempts per 15 minutes, 30 minute lockout
  const rateLimiter = useRateLimiter({
    maxAttempts: 5,
    windowMs: 15 * 60 * 1000,
    lockoutMs: 30 * 60 * 1000,
  });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    // Check rate limit before attempting login
    if (!rateLimiter.canAttempt) {
      const minutes = Math.ceil(rateLimiter.remainingLockTime / 60000);
      setError(`Demasiados intentos fallidos. Por favor espera ${minutes} minuto${minutes !== 1 ? 's' : ''} antes de intentar de nuevo.`);
      return;
    }

    setLoading(true);

    try {
      await signIn(email, password);
      // Success - reset rate limiter
      rateLimiter.reset();
      // Navigation handled by AuthContext state change
    } catch (err: unknown) {
      // Banned accounts (suspended access) get their own screen, not a
      // rate-limited error message. Branch on the error code, never on
      // message text.
      if (isAuthApiError(err) && err.code === 'user_banned') {
        setSuspended(true);
        return;
      }

      // Record failed attempt
      rateLimiter.recordAttempt(false);

      logger.error('[LoginForm] Login failed');
      const errorMessage = err instanceof Error ? err.message : 'Error al iniciar sesión. Verifica tus credenciales.';

      // Add remaining attempts warning if close to lockout
      if (rateLimiter.remainingAttempts <= 2 && rateLimiter.remainingAttempts > 0) {
        setError(`${errorMessage} (${rateLimiter.remainingAttempts} intento${rateLimiter.remainingAttempts !== 1 ? 's' : ''} restante${rateLimiter.remainingAttempts !== 1 ? 's' : ''})`);
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setSuspended(false);
    setPassword('');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-md p-8 bg-white rounded-2xl shadow-2xl"
    >
      {suspended ? (
        <SuspensionNotice onBack={handleBack} />
      ) : (
        <>
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-[#5D1828] to-[#7A2235] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <LogIn size={32} className="text-white" />
            </div>
            <h1 className="text-3xl font-bold text-[#111827] mb-2">
              Bienvenido
            </h1>
            <p className="text-[#6B7280]">
              Inicia sesión en tu cuenta
            </p>
          </div>

          <AnimatePresence mode="wait">
            {error && (
              <motion.div
                key="error-message"
                initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                animate={{ opacity: 1, height: 'auto', marginBottom: 16 }}
                exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 text-red-700 text-sm overflow-hidden"
              >
                <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </motion.div>
            )}
          </AnimatePresence>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-2">
                Correo electrónico
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={20} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-[#D1D5DB] rounded-lg focus:ring-2 focus:ring-[#5D1828] focus:border-transparent transition-all"
                  placeholder="tu@email.com"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#374151] mb-2">
                Contraseña
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={20} />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-[#D1D5DB] rounded-lg focus:ring-2 focus:ring-[#5D1828] focus:border-transparent transition-all"
                  placeholder="••••••••"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="flex items-center justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-[#5D1828] hover:text-[#7A2235] font-medium transition-colors"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading || rateLimiter.isLocked}
              className="w-full py-3 bg-[#5D1828] text-white rounded-lg font-medium hover:bg-[#7A2235] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-[#5D1828]/30 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Iniciando sesión...
                </>
              ) : rateLimiter.isLocked ? (
                <>
                  <Clock size={20} />
                  Cuenta bloqueada temporalmente
                </>
              ) : (
                <>
                  <LogIn size={20} />
                  Iniciar sesión
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-[#6B7280]">
            ¿No tienes cuenta?{' '}
            <Link
              to="/signup"
              className="text-[#5D1828] font-semibold hover:text-[#7A2235] transition-colors"
            >
              Regístrate aquí
            </Link>
          </div>
        </>
      )}
    </motion.div>
  );
}
